<script setup lang="ts">
import DeviceDrawerSection from '@/components/DeviceComponents/DeviceDrawerSection.vue'
import { EVENTS_ROUTES } from '@/router/routes'
import { EEventActions } from '@/store/eventStore/EventStoreTypes'
import { EStoreModules } from '@/store/storeType'
import type { DropZoneData } from '@/types/components/DropZoneData'
import { EHubProvider } from '@/types/enum/HubProviderEnum'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { DetectionModel } from '@/types/models/DetectionModel'
import { type AssociatedDeviceModel, type DeviceModel } from '@/types/models/DeviceModel'
import { defaultEventModel, type EventModel } from '@/types/models/EventModel'
import { ECheckpointsPerm } from '@/types/permissions/CheckpointsPermissionsEnum'
import CheckpointsSection from '@/views/EventsView/Detail/components/CheckpointsSection.vue'
import EventDatesSection from '@/views/EventsView/Detail/components/EventDatesSection.vue'
import { FullScreenLoader, StackLayout } from '@rfc/rfc-components-library'
import { type PermissionsEnums } from '@rfc/rfc-components-library/types'
import { useWindowSize, type Position } from '@vueuse/core'
import moment from 'moment'
import { computed, inject, onMounted, provide, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import type { SocketDetectionModel } from '../../types/socket/SocketDetectionModel'
import EventActions from './Detail/Actions/EventActions.vue'
import EventSocketWrapper from './Detail/EventSocketWrapper.vue'
import EventInformationsSection from './Detail/components/EventInformationsSection.vue'

const store = useStore()

type Props = {
    detections?: DetectionModel[]
    deletePermission?: PermissionsEnums
    editPermission?: PermissionsEnums
}
defineProps<Props>()
type Emits = {
    (e: 'delete', checkpoint: CheckpointModel): void
    (e: 'click', checkpoint?: CheckpointModel): void
    (e: 'dragStart', device?: AssociatedDeviceModel, checkpointId?: number): void
    (e: 'dragEnd', position?: Position): void
    (e: 'close'): void
    (e: 'refresh'): void
}
defineEmits<Emits>()

const { width } = useWindowSize()
const isMobile = computed(() => width.value < 768)
const route = useRoute()
const eventId = computed(() => parseInt(route.params.event_id.toString()))
const eventStore = computed(() => store?.state.event)

const data = ref<EventModel>({ ...defaultEventModel })
const getEvent = () =>
    store
        ?.dispatch(`${EStoreModules.EVENT}/${EEventActions.FETCH_EVENT}`, eventId.value)
        .then((res: EventModel) => {
            data.value = { ...res }
            dispatchEvent(
                new CustomEvent('recentlyVisited', {
                    bubbles: true,
                    detail: {
                        label: res.name,
                        organization_id: res.organization_id,
                        to: EVENTS_ROUTES.buildUrl(res.id)
                    }
                })
            )
        })

onMounted(() => getEvent())

const partnerConfig = computed(() =>
    eventStore.value?.data?.event?.partner?.config ? eventStore.value.data.event.partner : undefined
)
const eventCheckpoints = ref<CheckpointModel[]>([])
const handleCheckpointsUpdate = (value?: CheckpointModel[]) =>
    (eventCheckpoints.value = value ?? [])
const associatedDevices = ref<number[]>()

const currentEvent = computed(() => !!data.value.id && moment(data.value.end_at).isAfter())

const currentEventDevices = ref<Partial<DeviceModel>[]>([])
const setCurrentEventDevices = (devicesValue: (AssociatedDeviceModel | DeviceModel)[]) =>
    (currentEventDevices.value = devicesValue)

const dropZone = ref<DropZoneData>({
    checkpointId: undefined,
    device: undefined,
    position: undefined
})
const dragStart = (device?: AssociatedDeviceModel | DeviceModel, checkpointId?: number) => {
    dropZone.value.device = device
    dropZone.value.checkpointId = checkpointId
}
const dragEnd = (position?: Position) => (dropZone.value.position = position)
const handleClose = () => (dropZone.value.device = undefined)

provide(EHubProvider.EVENT_DEVICES, {
    devices: currentEventDevices,
    setDevices: setCurrentEventDevices
})

const isViewerOnly = inject<boolean>(EHubProvider.IS_VIEWER_ONLY, false)

const socketRef = ref<{ detections: SocketDetectionModel }>()
const socketDetections = computed(() => socketRef.value?.detections)
</script>

<template>
    <StackLayout direction="column" :gap="8" class="relative">
        <FullScreenLoader :loading="!!eventStore?.loading || !!store?.state.checkpoint.loading" />
        <EventSocketWrapper
            v-if="currentEvent"
            ref="socketRef"
            :devices="currentEventDevices"
            :checkpoints="eventCheckpoints" />
        <StackLayout gap="16" class="event-page overflow-y-visible" isResponsive>
            <StackLayout
                direction="column"
                gap="16"
                :class="['transition-all', isMobile ? 'w-full' : 'w-1/5 min-w-[340px]']">
                <StackLayout direction="column" gap="16" isResponsive class="w-full h-full">
                    <EventInformationsSection :data="data" @refresh="getEvent" />
                    <EventDatesSection :data="data" />
                    <EventActions v-if="!isViewerOnly" :data="data" @refresh="getEvent" />
                    <DeviceDrawerSection
                        v-if="!isViewerOnly && data && !isMobile"
                        class="overflow-y-auto"
                        :event="data"
                        :dropZoneData="dropZone"
                        @dragStart="dragStart"
                        @dragEnd="dragEnd"
                        @refresh="getEvent"
                        @close="handleClose" />
                </StackLayout>
            </StackLayout>
            <StackLayout direction="column" gap="16" class="w-full overflow-x-auto">
                <CheckpointsSection
                    class="h-full"
                    :eventData="data"
                    :detections="socketDetections?.checkpoint_detections ?? []"
                    :partnerConfig="partnerConfig"
                    :dropZone="dropZone"
                    :deletePermission="ECheckpointsPerm.CHECKPOINTS_DELETE"
                    @dragStart="dragStart"
                    @dragEnd="dragEnd"
                    @refresh="getEvent"
                    @checkpointUpdate="handleCheckpointsUpdate"
                    @refreshDevices="associatedDevices = $event"
                    @close="handleClose" />
            </StackLayout>
        </StackLayout>
    </StackLayout>
</template>

<style scoped lang="scss">
.event-page {
    height: calc(100vh - var(--navbar-height) - 48px);

    @media (max-width: 761px) {
        height: auto;
    }
}
</style>
